import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { id: "all",      label: "All",      icon: "☕", color: "#a78bfa" },
  { id: "critical", label: "Breaking", icon: "🔴", color: "#f87171" },
  { id: "whale",    label: "Whale",    icon: "🐋", color: "#22d3ee" },
  { id: "earnings", label: "Earnings", icon: "📊", color: "#4ade80" },
  { id: "macro",    label: "Macro",    icon: "🌐", color: "#fbbf24" },
];

const SENT_COLOR = {
  bullish: "#4ade80", bearish: "#f87171", mixed: "#fbbf24", neutral: "#8b949e",
};
const SENT_ICON = { bullish: "▲", bearish: "▼", mixed: "◆", neutral: "—" };

const BIAS_PCT = {
  bullish_strong: 92, bullish: 70, neutral: 50, bearish: 30, bearish_strong: 8,
};

function relTime(iso) {
  if (!iso) return "";
  try {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
  } catch { return ""; }
}

export default function OmegaTea({ compact = false, initialTicker = null, onTickerHandled }) {
  const [filter,         setFilter]         = useState("all");
  const [feed,           setFeed]           = useState({ articles: [], count: 0 });
  const [loading,        setLoading]        = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);
  const [selected,       setSelected]       = useState(null);
  const [insight,        setInsight]        = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [question,       setQuestion]       = useState("");
  const [chat,           setChat]           = useState([]);
  const [asking,         setAsking]         = useState(false);
  const [pinnedTicker,   setPinnedTicker]   = useState(initialTicker || null);
  const [tickerInput,    setTickerInput]    = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (initialTicker) {
      setPinnedTicker(String(initialTicker).toUpperCase());
      setSelected(null);
      onTickerHandled?.();
    }
  }, [initialTicker, onTickerHandled]);

  const loadFeed = useCallback(async (f, isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const url    = pinnedTicker ? `${API}/omega/tea/ticker/${pinnedTicker}` : `${API}/omega/tea/feed`;
      const params = pinnedTicker ? { limit: 12 } : { filter: f, limit: 24 };
      const r = await axios.get(url, { params });
      setFeed(r.data || { articles: [] });
    } catch {} finally { setLoading(false); setRefreshing(false); }
  }, [pinnedTicker]);

  useEffect(() => { loadFeed(filter); }, [filter, loadFeed]);

  useEffect(() => {
    if (selected) return undefined;
    const iv = setInterval(() => loadFeed(filter, true), 90000);
    return () => clearInterval(iv);
  }, [filter, selected, loadFeed]);

  const openArticle = async (a) => {
    setSelected(a); setInsight(null); setChat([]); setQuestion(""); setInsightLoading(true);
    try {
      const r = await axios.get(`${API}/omega/tea/article/${a.id}`);
      setInsight(r.data);
    } catch {} finally { setInsightLoading(false); }
  };

  const ask = async (e) => {
    e?.preventDefault();
    const q = question.trim();
    if (!q || asking || !selected) return;
    setAsking(true);
    setChat(prev => [...prev, { q, a: null }]);
    setQuestion("");
    try {
      const r = await axios.post(`${API}/omega/tea/ask`, { article_id: selected.id, question: q });
      setChat(prev => { const n = [...prev]; n[n.length - 1] = { q, a: r.data?.answer || "(no answer)" }; return n; });
    } catch {
      setChat(prev => { const n = [...prev]; n[n.length - 1] = { q, a: "Omega is offline. Try again." }; return n; });
    } finally { setAsking(false); }
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
  };

  // ─── ARTICLE READER ────────────────────────────────────────────────────────
  if (selected) {
    const ins      = insight?.insight || {};
    const biasPct  = BIAS_PCT[ins.forecastBias] ?? 50;
    const biasClr  = ins.forecastBias?.startsWith("bullish") ? "#4ade80"
                   : ins.forecastBias?.startsWith("bearish") ? "#f87171" : "#fbbf24";
    const sentClr  = SENT_COLOR[selected.sentiment] || "#8b949e";

    return (
      <div data-testid="tea-article-reader" style={{ paddingBottom: 32 }}>
        {/* Back bar */}
        <button
          data-testid="tea-back"
          onClick={() => { setSelected(null); setInsight(null); setChat([]); }}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "10px 0", marginBottom: 16, background: "none", border: "none",
            color: "#a78bfa", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>‹</span> Back to Tea
        </button>

        {/* Hero card */}
        <div style={{
          borderRadius: 20, overflow: "hidden", marginBottom: 14,
          border: `1px solid ${sentClr}30`,
          background: `linear-gradient(160deg, ${sentClr}12 0%, rgba(10,10,20,0.9) 60%)`,
        }}>
          {/* Accent top stripe */}
          <div style={{ height: 3, background: `linear-gradient(90deg, ${sentClr}, ${sentClr}44)` }} />
          <div style={{ padding: "18px 18px 16px" }}>
            {/* Badges */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              <Badge bg="rgba(167,139,250,0.18)" color="#a78bfa" border="rgba(167,139,250,0.35)">{selected.ticker}</Badge>
              <Badge bg={`${sentClr}22`} color={sentClr} border={`${sentClr}55`}>
                {SENT_ICON[selected.sentiment]} {(selected.sentiment || "").toUpperCase()}
              </Badge>
              {(selected.tags || []).map(t => (
                <Badge key={t} bg="rgba(255,255,255,0.05)" color="rgba(255,255,255,0.45)" border="rgba(255,255,255,0.10)">
                  {t.toUpperCase()}
                </Badge>
              ))}
              {selected.importance === "critical" && (
                <Badge bg="rgba(248,113,113,0.18)" color="#f87171" border="rgba(248,113,113,0.40)">
                  🔴 CRITICAL
                </Badge>
              )}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 10px", lineHeight: 1.3, color: "#fff" }}>
              {selected.title}
            </h2>
            <div style={{ display: "flex", gap: 8, fontSize: 11, color: "rgba(255,255,255,0.40)", flexWrap: "wrap" }}>
              <span>{selected.source || "—"}</span>
              {selected.pubDate && <><span>·</span><span>{relTime(selected.pubDate)} ago</span></>}
              {selected.link && (
                <a href={selected.link} target="_blank" rel="noopener noreferrer"
                   style={{ color: "#60a5fa", textDecoration: "none", marginLeft: "auto", fontWeight: 700 }}>
                  Full source →
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Omega Insight card */}
        {insightLoading && (
          <div style={{
            padding: "20px 18px", borderRadius: 18, marginBottom: 12,
            background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.15)",
            textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: 13,
          }}>
            <span style={{ display: "inline-block", marginRight: 8 }}>Ω</span>
            Brewing the insight…
          </div>
        )}

        {!insightLoading && insight && (
          <div style={{
            borderRadius: 20, overflow: "hidden", marginBottom: 12,
            background: "linear-gradient(160deg, rgba(167,139,250,0.08), rgba(96,165,250,0.04))",
            border: "1px solid rgba(167,139,250,0.20)",
          }}>
            {/* Purple top stripe */}
            <div style={{ height: 2, background: "linear-gradient(90deg, #a78bfa, #60a5fa44)" }} />
            <div style={{ padding: "16px 18px 18px" }}>
              {/* Omega header */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 10,
                  background: "linear-gradient(135deg, #a78bfa, #60a5fa)",
                  display: "grid", placeItems: "center",
                  fontSize: 15, fontWeight: 900, color: "#fff", flexShrink: 0,
                }}>Ω</div>
                <div>
                  <div style={{ fontSize: 9, letterSpacing: 2, color: "#a78bfa", fontWeight: 900 }}>OMEGA ZI · TRADER INSIGHT</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.30)", marginTop: 1 }}>
                    {insight.cached ? "cached" : "fresh"} · {ins.model || "AI"}
                  </div>
                </div>
              </div>

              {/* TL;DR */}
              <div style={{
                fontSize: 16, fontWeight: 700, color: "#fff",
                lineHeight: 1.5, marginBottom: 16,
                padding: "12px 14px", borderRadius: 12,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
              }}>
                {ins.tlDr || "No takeaway available."}
              </div>

              {/* Forecast bias bar */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 9, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", fontWeight: 700 }}>
                    FORECAST · {String(ins.horizon || "SWING").toUpperCase()}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: biasClr }}>
                    {String(ins.forecastBias || "neutral").replace("_", " ").toUpperCase()}
                    {ins.forecastConfidence ? ` · ${ins.forecastConfidence}%` : ""}
                  </span>
                </div>
                <div style={{ height: 7, borderRadius: 999, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${biasPct}%`,
                    background: `linear-gradient(90deg, ${biasClr}66, ${biasClr})`,
                    transition: "width 700ms cubic-bezier(0.2,0.8,0.2,1)",
                  }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 9, color: "rgba(255,255,255,0.25)" }}>
                  <span>BEARISH</span><span>NEUTRAL</span><span>BULLISH</span>
                </div>
              </div>

              {ins.traderInsight  && <InsightRow label="TRADER VIEW"      body={ins.traderInsight}  color="#60a5fa" />}
              {ins.hedgeFundView  && <InsightRow label="HEDGE-FUND DESK"  body={ins.hedgeFundView}  color="#a78bfa" />}
              {ins.whaleSignal    && <InsightRow label="WHALE SIGNAL"     body={ins.whaleSignal}    color="#22d3ee" />}

              {(ins.riskFlags || []).length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 9, letterSpacing: 1.5, color: "#f87171", fontWeight: 800, marginBottom: 8 }}>⚠ RISK FLAGS</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {ins.riskFlags.map((f, i) => (
                      <span key={i} style={{
                        padding: "4px 10px", borderRadius: 6,
                        background: "rgba(248,113,113,0.10)", color: "#f87171",
                        border: "1px solid rgba(248,113,113,0.22)", fontSize: 11,
                      }}>{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ask Omega */}
        <div style={{
          borderRadius: 20, overflow: "hidden",
          background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)",
        }}>
          <div style={{ height: 2, background: "linear-gradient(90deg, #60a5fa, #a78bfa44)" }} />
          <div style={{ padding: "14px 16px 16px" }}>
            <div style={{ fontSize: 9, letterSpacing: 2, color: "rgba(255,255,255,0.35)", fontWeight: 800, marginBottom: 12 }}>
              ASK OMEGA
            </div>
            <div style={{ marginBottom: chat.length ? 12 : 0 }}>
              {chat.map((m, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={{
                    padding: "9px 13px", borderRadius: "12px 12px 4px 12px", marginBottom: 4,
                    background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.18)",
                    color: "#fff", fontSize: 13, fontWeight: 600,
                  }}>{m.q}</div>
                  <div style={{
                    padding: "10px 13px", borderRadius: "4px 12px 12px 12px",
                    background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.18)",
                    color: "rgba(255,255,255,0.85)", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap",
                  }}>
                    {m.a == null
                      ? <span style={{ color: "#a78bfa" }}>Omega is thinking…</span>
                      : m.a}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <form onSubmit={ask} style={{ display: "flex", gap: 8 }}>
              <input
                data-testid="tea-ask-input"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder="Ask Omega about this…"
                disabled={asking}
                style={{
                  flex: 1, padding: "11px 14px", borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.04)", color: "#fff",
                  fontSize: 13, fontFamily: "inherit", outline: "none",
                }}
              />
              <button
                data-testid="tea-ask-submit"
                type="submit"
                disabled={!question.trim() || asking}
                style={{
                  padding: "11px 18px", borderRadius: 12, border: "none",
                  background: "linear-gradient(135deg, #a78bfa, #60a5fa)",
                  color: "#fff", fontWeight: 700, fontSize: 13,
                  cursor: asking ? "wait" : "pointer", fontFamily: "inherit",
                  opacity: !question.trim() || asking ? 0.55 : 1,
                  transition: "opacity 150ms",
                }}
              >{asking ? "…" : "Ask"}</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ─── FEED VIEW ─────────────────────────────────────────────────────────────
  const sentCounts  = feed.articles.reduce((a, x) => { a[x.sentiment] = (a[x.sentiment] || 0) + 1; return a; }, {});
  const total       = feed.articles.length || 1;
  const bullPct     = Math.round(((sentCounts.bullish || 0) / total) * 100);
  const bearPct     = Math.round(((sentCounts.bearish || 0) / total) * 100);
  const neutralPct  = Math.max(0, 100 - bullPct - bearPct);
  const breaking    = feed.articles.filter(a => a.importance === "critical").slice(0, 6);
  const activeFilter = FILTERS.find(f => f.id === filter) || FILTERS[0];

  return (
    <div data-testid="omega-tea" style={{ paddingBottom: 24 }}>

      {/* ── HEADER ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: "linear-gradient(135deg, #a78bfa, #60a5fa)",
              display: "grid", placeItems: "center",
              fontSize: 13, fontWeight: 900, color: "#fff",
            }}>Ω</div>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#a78bfa", fontWeight: 900 }}>OMEGA TEA</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>Market Intelligence</div>
            </div>
          </div>
          <button
            data-testid="tea-refresh"
            onClick={() => loadFeed(filter, true)}
            disabled={refreshing}
            style={{
              width: 34, height: 34, borderRadius: 10,
              background: "rgba(167,139,250,0.12)", color: "#a78bfa",
              border: "1px solid rgba(167,139,250,0.25)",
              fontSize: 15, cursor: refreshing ? "wait" : "pointer",
              display: "grid", placeItems: "center",
            }}
          >{refreshing ? "·" : "↻"}</button>
        </div>

        {/* Sentiment bar */}
        {feed.articles.length > 0 && (
          <div style={{
            padding: "10px 14px", borderRadius: 14,
            background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7, alignItems: "center" }}>
              <span style={{ fontSize: 9, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", fontWeight: 700 }}>
                MARKET MOOD · {feed.articles.length} STORIES
              </span>
              <div style={{ display: "flex", gap: 10, fontSize: 11, fontWeight: 800 }}>
                <span style={{ color: "#4ade80" }}>▲ {bullPct}%</span>
                <span style={{ color: "rgba(255,255,255,0.25)" }}>— {neutralPct}%</span>
                <span style={{ color: "#f87171" }}>▼ {bearPct}%</span>
              </div>
            </div>
            <div style={{ display: "flex", height: 5, borderRadius: 999, overflow: "hidden", gap: 1 }}>
              <div style={{ width: `${bullPct}%`,    background: "#4ade80", transition: "width 400ms ease", borderRadius: "999px 0 0 999px" }} />
              <div style={{ width: `${neutralPct}%`, background: "rgba(255,255,255,0.15)", transition: "width 400ms ease" }} />
              <div style={{ width: `${bearPct}%`,    background: "#f87171", transition: "width 400ms ease", borderRadius: "0 999px 999px 0" }} />
            </div>
          </div>
        )}
      </div>

      {/* ── BREAKING TAPE ── */}
      {breaking.length > 0 && (
        <div data-testid="tea-breaking-tape" style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
          padding: "8px 12px", borderRadius: 12,
          background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.22)",
          overflow: "hidden",
        }}>
          <span style={{
            flexShrink: 0, fontSize: 8, fontWeight: 900, letterSpacing: 1.5,
            padding: "3px 7px", borderRadius: 5,
            background: "#f87171", color: "#fff",
          }}>● LIVE</span>
          <div style={{ flex: 1, overflow: "hidden", position: "relative", height: 18 }}>
            <div style={{
              position: "absolute", whiteSpace: "nowrap",
              animation: `teaMarquee ${Math.max(20, breaking.length * 8)}s linear infinite`,
              display: "flex", gap: 28, alignItems: "center",
            }}>
              {[...breaking, ...breaking].map((a, i) => (
                <button key={`${a.id}-${i}`} onClick={() => openArticle(a)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "rgba(255,255,255,0.80)", fontSize: 11, fontFamily: "inherit",
                    display: "flex", gap: 7, alignItems: "center", padding: 0,
                  }}
                >
                  <span style={{ color: "#f87171", fontWeight: 800, fontSize: 10 }}>{a.ticker}</span>
                  <span>{a.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── FILTER TABS ── */}
      <div style={{ marginBottom: 14 }}>
        {/* Ticker search */}
        {!pinnedTicker && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const v = tickerInput.trim().toUpperCase();
              if (v && /^[A-Z.\-]{1,8}$/.test(v)) { setPinnedTicker(v); setTickerInput(""); }
            }}
            style={{
              display: "flex", gap: 0, marginBottom: 10, borderRadius: 12, overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.04)",
            }}
          >
            <span style={{ padding: "0 10px", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.30)", fontSize: 13 }}>🔍</span>
            <input
              name="q"
              data-testid="tea-ticker-search-input"
              value={tickerInput}
              onChange={e => setTickerInput(e.target.value.toUpperCase())}
              placeholder="Search ticker — e.g. AAL"
              maxLength={8}
              style={{
                flex: 1, padding: "10px 0", background: "none", border: "none", outline: "none",
                color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: 1, fontFamily: "inherit",
              }}
            />
            <button type="submit" style={{
              padding: "10px 14px", background: "#a78bfa", color: "#fff",
              border: "none", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
            }}>Go</button>
          </form>
        )}

        {pinnedTicker ? (
          <button
            data-testid="tea-pinned-ticker"
            onClick={() => { setPinnedTicker(null); setTickerInput(""); }}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 14px", borderRadius: 999,
              border: "1px solid rgba(167,139,250,0.45)", background: "rgba(167,139,250,0.14)",
              color: "#a78bfa", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}
          >
            ☕ {pinnedTicker} · <span style={{ opacity: 0.55 }}>clear ✕</span>
          </button>
        ) : (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
            {FILTERS.map(f => {
              const act = f.id === filter;
              return (
                <button
                  key={f.id}
                  data-testid={`tea-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  style={{
                    flexShrink: 0, display: "flex", alignItems: "center", gap: 5,
                    padding: "8px 14px", borderRadius: 12, fontFamily: "inherit",
                    background: act ? `${f.color}20` : "rgba(255,255,255,0.04)",
                    border: `1.5px solid ${act ? f.color + "70" : "rgba(255,255,255,0.07)"}`,
                    color: act ? f.color : "rgba(255,255,255,0.45)",
                    fontSize: 12, fontWeight: act ? 800 : 600,
                    cursor: "pointer", transition: "all 180ms ease",
                    boxShadow: act ? `0 0 12px ${f.color}25` : "none",
                  }}
                >
                  <span>{f.icon}</span>
                  <span style={{ letterSpacing: 0.3 }}>{f.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── ARTICLE LIST ── */}
      {loading && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "rgba(255,255,255,0.25)", fontSize: 13 }}>
          <span style={{ display: "block", fontSize: 22, marginBottom: 8 }}>☕</span>
          Brewing the latest tea…
        </div>
      )}
      {!loading && feed.articles.length === 0 && (
        <div data-testid="tea-empty" style={{ padding: "40px 0", textAlign: "center", color: "rgba(255,255,255,0.25)", fontSize: 13 }}>
          No tea under this filter. Try "All".
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {feed.articles.map((a, idx) => {
          const sent  = SENT_COLOR[a.sentiment] || "#8b949e";
          const isCrit = a.importance === "critical";
          const isFirst = idx === 0;

          return (
            <button
              key={a.id}
              data-testid={`tea-card-${a.id}`}
              onClick={() => openArticle(a)}
              style={{
                display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                padding: 0, background: "none", border: "none", fontFamily: "inherit",
              }}
            >
              <div style={{
                borderRadius: 16,
                background: isFirst
                  ? `linear-gradient(145deg, ${sent}18 0%, rgba(15,15,28,0.95) 55%)`
                  : "rgba(255,255,255,0.028)",
                border: `1px solid ${isCrit ? "#f8717155" : isFirst ? sent + "30" : "rgba(255,255,255,0.07)"}`,
                overflow: "hidden",
                boxShadow: isCrit ? "0 0 18px rgba(248,113,113,0.12)" : "none",
                transition: "border-color 180ms",
              }}>
                {/* Left accent + content */}
                <div style={{ display: "flex" }}>
                  {/* Accent stripe */}
                  <div style={{
                    width: 3, flexShrink: 0,
                    background: `linear-gradient(180deg, ${sent}, ${sent}44)`,
                  }} />

                  <div style={{ flex: 1, padding: "13px 13px 11px" }}>
                    {/* Top row: badges */}
                    <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{
                        fontSize: 9, fontWeight: 900, letterSpacing: 1.2, padding: "2px 7px",
                        borderRadius: 5, background: "rgba(167,139,250,0.18)", color: "#a78bfa",
                        border: "1px solid rgba(167,139,250,0.25)",
                      }}>{a.ticker}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 800, letterSpacing: 1.0, padding: "2px 7px",
                        borderRadius: 5, background: `${sent}22`, color: sent,
                        border: `1px solid ${sent}40`,
                        display: "flex", alignItems: "center", gap: 3,
                      }}>
                        {SENT_ICON[a.sentiment]} {(a.sentiment || "").toUpperCase()}
                      </span>
                      {(a.tags || []).slice(0, 2).map(t => (
                        <span key={t} style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: 0.8, padding: "2px 7px",
                          borderRadius: 5, background: "rgba(255,255,255,0.05)",
                          color: "rgba(255,255,255,0.35)", border: "1px solid rgba(255,255,255,0.08)",
                        }}>{t.toUpperCase()}</span>
                      ))}
                      {isCrit && (
                        <span style={{
                          fontSize: 9, fontWeight: 900, letterSpacing: 1.0, padding: "2px 7px",
                          borderRadius: 5, background: "rgba(248,113,113,0.16)", color: "#f87171",
                          border: "1px solid rgba(248,113,113,0.35)", marginLeft: "auto",
                        }}>🔴 LIVE</span>
                      )}
                    </div>

                    {/* Title */}
                    <div style={{
                      fontSize: isFirst ? 15 : 14,
                      fontWeight: isFirst ? 800 : 700,
                      color: "#fff", lineHeight: 1.35,
                      marginBottom: 9,
                      display: "-webkit-box", WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical", overflow: "hidden",
                    }}>
                      {a.title}
                    </div>

                    {/* Bottom meta row */}
                    <div style={{
                      display: "flex", alignItems: "center", gap: 6,
                      fontSize: 10, color: "rgba(255,255,255,0.28)",
                    }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%" }}>
                        {a.source || "—"}
                      </span>
                      {a.pubDate && (
                        <><span>·</span><span style={{ flexShrink: 0 }}>{relTime(a.pubDate)} ago</span></>
                      )}
                      <span style={{
                        marginLeft: "auto", flexShrink: 0, fontSize: 10, fontWeight: 700,
                        color: "#a78bfa", display: "flex", alignItems: "center", gap: 3,
                      }}>
                        Omega insight ›
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Badge({ children, bg, color, border }) {
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 800, letterSpacing: 1.2,
      background: bg, color, border: `1px solid ${border}`,
    }}>{children}</span>
  );
}

function InsightRow({ label, body, color }) {
  if (!body) return null;
  return (
    <div style={{
      marginBottom: 12, padding: "11px 13px", borderRadius: 12,
      background: `${color}08`, border: `1px solid ${color}20`,
    }}>
      <div style={{ fontSize: 9, letterSpacing: 1.5, color, fontWeight: 900, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.80)", lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}
